import { api } from './api.js';

const REASON_MIN = 3;
const REASON_MAX = 500;

export function parseAdminError(error) {
  if (!error) return null;
  if (error.body && typeof error.body === 'object') return error.body;
  if (typeof error.body === 'string') {
    try { return JSON.parse(error.body); } catch { /* plain-text response */ }
  }
  return null;
}

function createDialog({ title, description }) {
  const overlay = document.createElement('div');
  overlay.className = 'admin-security-overlay';
  overlay.innerHTML = `
    <div class="admin-security-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-security-title">
      <h2 id="admin-security-title"></h2>
      <p class="admin-security-description"></p>
      <div class="admin-security-content"></div>
    </div>
  `;
  overlay.querySelector('h2').textContent = title;
  overlay.querySelector('.admin-security-description').textContent = description;
  document.body.appendChild(overlay);
  return {
    overlay,
    content: overlay.querySelector('.admin-security-content'),
    close: () => overlay.remove(),
  };
}

export function requestAdminReason(actionLabel) {
  return new Promise((resolve) => {
    const dialog = createDialog({
      title: 'Record a reason',
      description: `Explain why you are about to ${actionLabel}. This is stored in the permanent admin audit log.`,
    });
    dialog.content.innerHTML = `
      <label class="admin-security-label" for="admin-security-reason">Operator reason</label>
      <textarea id="admin-security-reason" rows="4" minlength="${REASON_MIN}" maxlength="${REASON_MAX}" required></textarea>
      <div class="admin-security-error" role="status"></div>
      <div class="admin-security-actions">
        <button type="button" class="btn admin-security-cancel">Cancel</button>
        <button type="button" class="btn admin-security-submit">Continue</button>
      </div>
    `;
    const input = dialog.content.querySelector('textarea');
    const error = dialog.content.querySelector('.admin-security-error');
    const finish = (value) => {
      dialog.close();
      resolve(value);
    };
    dialog.content.querySelector('.admin-security-cancel').addEventListener('click', () => finish(null));
    dialog.content.querySelector('.admin-security-submit').addEventListener('click', () => {
      const reason = input.value.trim();
      if (reason.length < REASON_MIN || reason.length > REASON_MAX) {
        error.textContent = `Enter between ${REASON_MIN} and ${REASON_MAX} characters.`;
        input.focus();
        return;
      }
      finish(reason);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') finish(null);
    });
    input.focus();
  });
}

function hasPasswordMethod(status) {
  return status.methods?.some((method) => method.type === 'password');
}

function passwordStepUp(apiClient) {
  return new Promise((resolve, reject) => {
    const dialog = createDialog({
      title: 'Confirm it’s you',
      description: 'This high-risk admin action requires a recent sign-in. Enter your current password to continue.',
    });
    dialog.content.innerHTML = `
      <label class="admin-security-label" for="admin-security-password">Current password</label>
      <input id="admin-security-password" type="password" autocomplete="current-password" maxlength="1024" required>
      <div class="admin-security-error" role="status"></div>
      <div class="admin-security-actions">
        <button type="button" class="btn admin-security-cancel">Cancel</button>
        <button type="button" class="btn admin-security-submit">Verify</button>
      </div>
    `;
    const input = dialog.content.querySelector('input');
    const error = dialog.content.querySelector('.admin-security-error');
    const submit = dialog.content.querySelector('.admin-security-submit');
    const finish = (value) => {
      dialog.close();
      resolve(value);
    };
    dialog.content.querySelector('.admin-security-cancel').addEventListener('click', () => finish(false));
    submit.addEventListener('click', async () => {
      if (!input.value) {
        error.textContent = 'Enter your current password.';
        input.focus();
        return;
      }
      submit.disabled = true;
      error.textContent = 'Verifying…';
      try {
        await apiClient.post('/api/admin/step-up/password', { password: input.value });
        finish(true);
      } catch (err) {
        const parsed = parseAdminError(err);
        if (parsed?.error === 'invalid_password') {
          input.value = '';
          error.textContent = 'Password not accepted. Try again.';
          input.focus();
          submit.disabled = false;
          return;
        }
        dialog.close();
        reject(err);
      }
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') submit.click();
      if (event.key === 'Escape') finish(false);
    });
    input.focus();
  });
}

function adminReturnTo() {
  const path = `${window.location.pathname}${window.location.search}`;
  return /^\/admin(?:\/|$)/.test(path) ? path : '/admin';
}

function providerLabel(provider) {
  if (provider === 'discord') return 'Discord';
  if (provider === 'twitch') return 'Twitch';
  return provider ? `${provider[0].toUpperCase()}${provider.slice(1)}` : 'your provider';
}

function interactiveProviderUrl(rawUrl, provider) {
  const interactiveParam = {
    discord: ['prompt', 'consent'],
    twitch: ['force_verify', 'true'],
  }[provider];
  if (!interactiveParam) return rawUrl;

  // Better Auth owns the state, PKCE verifier, scopes, and callback. Change
  // only the provider-specific prompt so admin step-up cannot complete via a
  // silent existing-provider session.
  const url = new URL(rawUrl);
  url.searchParams.set(...interactiveParam);
  return url.toString();
}

function providerStepUp(apiClient, status, navigate) {
  return new Promise((resolve, reject) => {
    const provider = (status.methods || [])
      .map((method) => method.provider)
      .find((candidate) => candidate && candidate !== 'credential');
    const label = providerLabel(provider);
    const dialog = createDialog({
      title: 'Confirm it’s you',
      description: `Reauthenticate with ${label} to continue this high-risk admin action.`,
    });
    dialog.content.innerHTML = `
      <div class="admin-security-error" role="status"></div>
      <div class="admin-security-actions">
        <button type="button" class="btn admin-security-cancel">Cancel</button>
        <button type="button" class="btn admin-security-submit"></button>
      </div>
    `;
    const submit = dialog.content.querySelector('.admin-security-submit');
    submit.textContent = `Continue with ${label}`;
    dialog.content.querySelector('.admin-security-cancel').addEventListener('click', () => {
      dialog.close();
      resolve(false);
    });
    submit.addEventListener('click', async () => {
      submit.disabled = true;
      try {
        if (!provider) throw new Error('No linked sign-in provider is available for reauthentication.');
        const callbackURL = adminReturnTo();
        const response = await apiClient.post('/api/auth/sign-in/social', {
          provider,
          callbackURL,
          errorCallbackURL: callbackURL,
        });
        if (response?.redirect !== true || typeof response.url !== 'string' || !response.url) {
          throw new Error(`Unable to start ${label} reauthentication.`);
        }
        navigate(interactiveProviderUrl(response.url, provider));
        dialog.close();
        resolve(false);
      } catch (err) {
        dialog.close();
        reject(err);
      }
    });
  });
}

export async function ensureRecentAdminAuth(
  apiClient = api,
  providerNavigator = (url) => window.location.assign(url),
) {
  const status = await apiClient.get('/api/admin/step-up');
  if (status.fresh) return true;
  return hasPasswordMethod(status)
    ? passwordStepUp(apiClient)
    : providerStepUp(apiClient, status, providerNavigator);
}

export async function runSensitiveAdminMutation({
  actionLabel,
  mutate,
  apiClient = api,
  reasonProvider = requestAdminReason,
  providerNavigator,
}) {
  const reason = await reasonProvider(actionLabel);
  if (!reason) return false;
  if (!await ensureRecentAdminAuth(apiClient, providerNavigator)) return false;

  try {
    await mutate(reason);
    return true;
  } catch (err) {
    if (parseAdminError(err)?.error !== 'admin_step_up_required') throw err;
    if (!await ensureRecentAdminAuth(apiClient, providerNavigator)) return false;
    await mutate(reason);
    return true;
  }
}
